import "./messages.css";

import {
	Add,
	ArrowRight,
	ArrowUp,
	AttachSquare,
	LogoutCurve,
	Music,
	People,
	Stickynote,
	Trash,
	User,
} from "iconsax-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import chatbotAvatar from "../../assets/images/chatbot-vector.png";
import defaultAvatar from "../../assets/images/messages-avatar.png";
import { useMatrix } from "../../context/MatrixContext";
import { api } from "../../utils/axios";
import UserProfile from "../home/userProfile";

const specialConversations = [
	{
		id: 0,
		name: "پیام های ذخیره شده",
		avatarType: "icon",
		icon: <Stickynote size={28} color="#7A7E9D" variant="Linear" />,
		lastMessage: "",
		unread: 0,
		pinned: false,
		isSpecial: true,
	},
	{
		id: -1,
		name: "چت‌بات",
		avatarType: "image",
		avatar: chatbotAvatar,
		lastMessage: "",
		unread: 0,
		pinned: false,
		isSpecial: true,
	},
];

const CHATBOT_ID = -1;
const SAVED_MESSAGES_ID = 0;
const MATRIX_DOMAIN = "chat.raah.work";

const AUTO_LEAVE_DUPLICATE_DMS = true;

function Messages() {
	const { client, isReady } = useMatrix();

	const [selectedId, setSelectedId] = useState(0);
	const [inputValue, setInputValue] = useState("");
	const [mobileChatOpen, setMobileChatOpen] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [filePreview, setFilePreview] = useState(null);
	const [historyLoading, setHistoryLoading] = useState(false);

	// Group Modals
	const [showGroupInfoModal, setShowGroupInfoModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion

	// Chatbot State
	const [chatbotMessages, setChatbotMessages] = useState(() => {
		const saved = localStorage.getItem("chatbotMessages");
		return saved
			? JSON.parse(saved)
			: [
					{
						sender: "them",
						text: "سلام! من چت‌بات هستم. چطور می‌توانم کمک کنم؟",
						time: "08:02",
					},
				];
	});

	const [matrixRoomId, setMatrixRoomId] = useState(null);
	const [matrixMessages, setMatrixMessages] = useState([]);
	const [matrixRooms, setMatrixRooms] = useState([]);

	const [recentConversations, setRecentConversations] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [showNewConversationModal, setShowNewConversationModal] =
		useState(false);

	// Group Chat State
	const [showGroupModal, setShowGroupModal] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [groupName, setGroupName] = useState("");

	const messageContainerRef = useRef();
	const userData = useSelector((state) => state.user.data);
	const teamUsers = userData?.team_users || [];

	const getUserById = (uid) =>
		teamUsers.find((u) => Number(u.uid) === Number(uid));
	const getMatrixId = (uid) => `@user_${uid}:${MATRIX_DOMAIN}`;

	// ------------------------------------------------------------------
	// 1. HELPER FUNCTIONS
	// ------------------------------------------------------------------
	const parseUidFromMatrixId = (mxid) => {
		const match = mxid.match(/@user_(\d+):/);
		return match ? parseInt(match[1], 10) : null;
	};

	const formatDateInPersian = (dateString) => {
		if (!dateString || dateString === "today") return "";
		const date = new Date(dateString);
		return new Intl.DateTimeFormat("fa-IR", {
			year: "numeric",
			month: "long",
			day: "numeric",
		}).format(date);
	};

	const shouldShowDateSeparator = (current, previous) => {
		if (!previous) return true;
		return current.date !== previous.date;
	};

	const getActiveMembers = (room) =>
		room
			.getMembers()
			.filter((m) => m.membership === "join" || m.membership === "invite");

	const getOtherMembers = (room) => {
		const myId = client?.getUserId();
		if (!myId) return [];
		return getActiveMembers(room).filter((m) => m.userId !== myId);
	};

	// ------------------------------------------------------------------
	// 2. MATRIX LOGIC & HANDLERS
	// ------------------------------------------------------------------

	const ensureJoined = async (roomId) => {
		if (!client || !roomId) return;
		const room = client.getRoom(roomId);
		const membership = room?.getMyMembership?.();
		if (membership === "join") return;
		try {
			await client.joinRoom(roomId);
		} catch (e) {
			console.error("ensureJoined failed:", roomId, membership, e);
			throw e;
		}
	};

	const findRoomForUser = (targetUid) => {
		if (!client || !isReady) return null;
		const rooms = client.getVisibleRooms();
		const myId = client.getUserId();

		if (targetUid === 0) {
			return rooms.find((r) => {
				if (r.getMyMembership?.() !== "join") return false;
				const active = getActiveMembers(r);
				return active.length === 1 && active[0].userId === myId;
			});
		}

		const targetMatrixId = getMatrixId(targetUid);
		const dmCandidates = rooms.filter((room) => {
			if (room.getMyMembership?.() !== "join") return false;
			const others = getOtherMembers(room);
			return others.length === 1 && others[0].userId === targetMatrixId;
		});

		dmCandidates.sort(
			(a, b) =>
				(b.getLastActiveTimestamp?.() || 0) -
				(a.getLastActiveTimestamp?.() || 0),
		);
		return dmCandidates[0] || null;
	};

	const findRoomById = (roomId) => {
		if (!client || !isReady) return null;
		return client.getRoom(roomId);
	};

	const findAllDmRoomsForUid = (uid) => {
		if (!client || !isReady) return [];
		const targetMxid = getMatrixId(uid);
		const rooms = client.getVisibleRooms();
		const candidates = rooms.filter((room) => {
			const others = getOtherMembers(room);
			if (others.length !== 1) return false;
			return others[0].userId === targetMxid;
		});
		candidates.sort(
			(a, b) =>
				(b.getLastActiveTimestamp?.() || 0) -
				(a.getLastActiveTimestamp?.() || 0),
		);
		return candidates;
	};

	const cleanupDuplicateDmRooms = async (uid, keepRoomId) => {
		if (!AUTO_LEAVE_DUPLICATE_DMS) return;
		if (!client || !isReady) return;
		if (!uid || !keepRoomId) return;
		try {
			const all = findAllDmRoomsForUid(uid);
			const toLeave = all
				.filter((r) => r.roomId !== keepRoomId)
				.filter((r) => r.getMyMembership?.() === "join");
			if (toLeave.length === 0) return;
			for (const r of toLeave) {
				try {
					await client.leave(r.roomId);
				} catch (e) {
					console.warn("Failed leaving duplicate room:", r.roomId, e);
				}
			}
			setMatrixRooms([...client.getVisibleRooms()]);
		} catch (e) {
			console.warn("cleanupDuplicateDmRooms error:", e);
		}
	};

	const waitForRoomToAppear = async (roomId, timeoutMs = 7000) => {
		const start = Date.now();
		while (Date.now() - start < timeoutMs) {
			const room = client?.getRoom(roomId);
			if (room) return room;
			await new Promise((r) => setTimeout(r, 200));
		}
		return null;
	};

	// ------------------------------------------------------------------
	// 3. MAPPING EVENTS & DATA
	// ------------------------------------------------------------------

	// ✅ Check if I am Admin
	const amIAdmin = useMemo(() => {
		if (!client || !matrixRoomId) return false;
		const room = client.getRoom(matrixRoomId);
		if (!room) return false;
		const me = room.getMember(client.getUserId());
		// In Matrix, power level 100 is usually Admin/Owner
		return me?.powerLevel >= 100;
	}, [client, matrixRoomId, matrixMessages]); // Re-calculate when room updates

	const mapMatrixEventToUI = (event) => {
		const senderMxId = event.getSender();
		const uid = parseUidFromMatrixId(senderMxId);
		const teamUser = uid ? getUserById(uid) : null;
		let senderName = senderMxId;
		if (teamUser) {
			senderName = `${teamUser.fname} ${teamUser.lname}`;
		} else {
			const room = client.getRoom(event.getRoomId());
			const member = room?.getMember(senderMxId);
			if (member?.name) senderName = member.name;
		}

		const content = event.getContent();
		const date = new Date(event.getTs());
		const isMe = event.getSender() === client.getUserId();

		let attachmentUrl = null;
		const fileName = content.body || "Attachment";
		let fileType = content.info?.mimetype || "application/octet-stream";

		if (["m.image", "m.file", "m.video", "m.audio"].includes(content.msgtype)) {
			if (content.url) {
				if (content.url.startsWith("blob:")) {
					attachmentUrl = content.url;
					if (!content.info?.mimetype && event.getContent().file?.type) {
						fileType = event.getContent().file.type;
					}
				} else {
					const cleanId = content.url.replace("mxc://", "");
					const baseUrl = client.getHomeserverUrl();
					attachmentUrl = `${baseUrl}/_matrix/media/v3/download/${cleanId}`;
				}
			}
		}

		return {
			sender: isMe ? "me" : "them",
			senderName: senderName,
			senderId: senderMxId,
			text: content.msgtype === "m.text" ? content.body : null,
			attachment: attachmentUrl,
			fileName: fileName,
			fileType: fileType,
			time: date.toLocaleTimeString("fa-IR", {
				hour: "2-digit",
				minute: "2-digit",
			}),
			date: date.toISOString().split("T")[0],
			fullDate: date.toISOString(),
			isUploading: event.isUploading || false,
		};
	};

	const displayedMessages = useMemo(() => {
		if (selectedId === CHATBOT_ID)
			return chatbotMessages.map((m) => ({ ...m, date: "today" }));
		return matrixMessages.map(mapMatrixEventToUI);
	}, [selectedId, chatbotMessages, matrixMessages]);

	const activeRoomMembers = useMemo(() => {
		if (!client || !matrixRoomId) return [];
		const room = client.getRoom(matrixRoomId);
		if (!room) return [];
		const members = room.getMembers().filter((m) => m.membership === "join");
		return members.map((m) => {
			const uid = parseUidFromMatrixId(m.userId);
			const teamUser = uid ? getUserById(uid) : null;
			return {
				userId: m.userId,
				name: teamUser
					? `${teamUser.fname} ${teamUser.lname}`
					: m.name || m.userId,
				avatar: teamUser?.profile_picture || null,
				powerLevel: m.powerLevel,
			};
		});
	}, [matrixRoomId, client, matrixMessages, teamUsers]);

	// ------------------------------------------------------------------
	// 4. LOGIC: DELETE OR LEAVE
	// ------------------------------------------------------------------

	// ✅ New Logic: If Admin -> Kick everyone then leave. If Member -> Just leave.
	const handleDeleteOrLeave = async () => {
		if (!client || !matrixRoomId) return;
		setIsDeleting(true);

		try {
			if (amIAdmin) {
				// --- ADMIN LOGIC: KICK EVERYONE ELSE ---
				const room = client.getRoom(matrixRoomId);
				const members = room
					.getMembers()
					.filter((m) => m.membership === "join");

				for (const member of members) {
					if (member.userId !== client.getUserId()) {
						try {
							// Kick user with reason
							await client.kick(
								matrixRoomId,
								member.userId,
								"Group deleted by admin",
							);
						} catch (kErr) {
							console.error("Failed to kick user:", member.userId, kErr);
						}
					}
				}

				await client.leave(matrixRoomId);
				await client.forget(matrixRoomId); // Clean up history
				toast.success("گروه با موفقیت حذف شد");
			} else {
				// --- MEMBER LOGIC: JUST LEAVE ---
				await client.leave(matrixRoomId);
				toast.success("از گروه خارج شدید");
			}

			// Cleanup State
			setSelectedId(0);
			setMatrixRoomId(null);
			setMobileChatOpen(false);
			setShowDeleteModal(false);
			setShowGroupInfoModal(false);
			setMatrixRooms([...client.getVisibleRooms()]);
		} catch (err) {
			console.error("Action failed:", err);
			toast.error("خطا در انجام عملیات");
		} finally {
			setIsDeleting(false);
		}
	};

	// ------------------------------------------------------------------
	// 5. USE EFFECTS
	// ------------------------------------------------------------------

	useEffect(() => {
		if (isReady && client) {
			const updateRooms = () => {
				const visible = client.getVisibleRooms();
				visible.forEach((room) => {
					if (room.getMyMembership() === "invite" && !room.__joining) {
						room.__joining = true;
						client.joinRoom(room.roomId).catch(console.error);
					}
				});
				setMatrixRooms([...visible]);
			};
			updateRooms();
			client.on("Room", updateRooms);
			client.on("Room.myMembership", updateRooms);
			return () => {
				client.removeListener("Room", updateRooms);
				client.removeListener("Room.myMembership", updateRooms);
			};
		}
	}, [isReady, client]);

	useEffect(() => {
		setMatrixMessages([]);
		setMatrixRoomId(null);
		if (!isReady || !client || selectedId === CHATBOT_ID) return;

		if (typeof selectedId === "string" && selectedId.startsWith("!")) {
			const room = findRoomById(selectedId);
			if (room) {
				ensureJoined(selectedId).catch(console.error);
				setMatrixRoomId(selectedId);
			}
			return;
		}

		const room = findRoomForUser(
			selectedId === SAVED_MESSAGES_ID ? 0 : selectedId,
		);
		if (room) {
			setMatrixRoomId(room.roomId);
			if (
				AUTO_LEAVE_DUPLICATE_DMS &&
				typeof selectedId === "number" &&
				selectedId > 0
			) {
				cleanupDuplicateDmRooms(selectedId, room.roomId);
			}
		}
	}, [selectedId, isReady, client, matrixRooms]);

	useEffect(() => {
		if (!matrixRoomId || !client) return;
		const room = client.getRoom(matrixRoomId);
		if (!room) return;

		setHistoryLoading(true);
		const updateTimeline = () => {
			const events = room.getLiveTimeline().getEvents();
			const msgs = events.filter((e) => e.getType() === "m.room.message");
			setMatrixMessages([...msgs]);
		};
		updateTimeline();

		client
			.scrollback(room, 50)
			.then(() => updateTimeline())
			.catch((err) => console.warn("History fetch failed:", err))
			.finally(() => setHistoryLoading(false));

		const onTimelineEvent = (event, thisRoom) => {
			if (
				thisRoom.roomId === matrixRoomId &&
				event.getType() === "m.room.message"
			) {
				setMatrixMessages((prev) => {
					if (prev.find((e) => e.getId() === event.getId())) return prev;
					let newMessages = [...prev];
					if (event.getSender() === client.getUserId()) {
						newMessages = newMessages.filter(
							(m) => !String(m.getId?.()).startsWith("temp-"),
						);
					}
					return [...newMessages, event];
				});
			}
		};

		client.on("Room.timeline", onTimelineEvent);
		return () => client.removeListener("Room.timeline", onTimelineEvent);
	}, [matrixRoomId, client]);

	useEffect(() => {
		if (messageContainerRef.current) {
			setTimeout(() => {
				if (messageContainerRef.current) {
					messageContainerRef.current.scrollIntoView({
						behavior: "smooth",
						block: "end",
					});
				}
			}, 100);
		}
	}, [selectedId, displayedMessages, historyLoading]);

	// ------------------------------------------------------------------
	// 6. USER ACTION HANDLERS (Send, Create Group)
	// ------------------------------------------------------------------
	const handleFileSelect = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		setSelectedFile(file);

		if (file.type.startsWith("image/")) {
			const reader = new FileReader();
			reader.onload = (ev) => setFilePreview(ev.target.result);
			reader.readAsDataURL(file);
		} else if (file.type.startsWith("video/")) {
			setFilePreview("VIDEO_PLACEHOLDER");
		} else if (file.type.startsWith("audio/")) {
			setFilePreview("AUDIO_PLACEHOLDER");
		} else {
			setFilePreview("FILE_PLACEHOLDER");
		}
	};

	const clearFile = () => {
		setSelectedFile(null);
		setFilePreview(null);
		const el = document.getElementById("file-input");
		if (el) el.value = "";
	};

	const handleSend = async (e) => {
		e.preventDefault();
		if (!inputValue.trim() && !selectedFile) return;

		if (selectedId === CHATBOT_ID) {
			const now = new Date();
			const time = now.toLocaleTimeString("fa-IR", {
				hour: "2-digit",
				minute: "2-digit",
			});
			setChatbotMessages((prev) => [
				...prev,
				{ sender: "me", text: inputValue, time },
			]);
			const textToSend = inputValue;
			setInputValue("");
			setIsLoading(true);
			try {
				const res = await api.post(
					"/chat",
					{ messages: [{ role: "user", content: textToSend }] },
					{
						headers: {
							Authorization: `Bearer ${localStorage.getItem("token")}`,
						},
					},
				);
				setChatbotMessages((prev) => [
					...prev,
					{ sender: "them", text: res.data.response, time },
				]);
			} catch {
				setChatbotMessages((prev) => [
					...prev,
					{ sender: "them", text: "خطا", time },
				]);
			} finally {
				setIsLoading(false);
			}
			return;
		}

		try {
			let roomId = matrixRoomId;
			if (!roomId && typeof selectedId === "number") {
				const existing = findRoomForUser(
					selectedId === SAVED_MESSAGES_ID ? 0 : selectedId,
				);
				if (existing) roomId = existing.roomId;
			}

			if (!roomId) {
				if (selectedId === SAVED_MESSAGES_ID) {
					const res = await client.createRoom({
						visibility: "private",
						name: "Saved Messages",
						topic: "Personal Notes",
					});
					roomId = res.room_id;
				} else if (typeof selectedId === "number") {
					const targetMatrixId = getMatrixId(selectedId);
					const res = await client.createRoom({
						visibility: "private",
						invite: [targetMatrixId],
						is_direct: true,
						preset: "trusted_private_chat",
						initial_state: [
							{
								type: "m.room.encryption",
								state_key: "",
								content: {
									algorithm: "m.megolm.v1.aes-sha2",
								},
							},
						],
					});
					roomId = res.room_id;
				} else {
					roomId = selectedId;
				}
				setMatrixRoomId(roomId);
				setMatrixRooms([...client.getVisibleRooms()]);
			}

			await ensureJoined(roomId);

			if (
				AUTO_LEAVE_DUPLICATE_DMS &&
				typeof selectedId === "number" &&
				selectedId > 0
			) {
				cleanupDuplicateDmRooms(selectedId, roomId);
			}

			const mockEvent = {
				getId: () => "temp-" + Date.now(),
				getContent: () => ({
					body: inputValue,
					msgtype: selectedFile
						? selectedFile.type.startsWith("image")
							? "m.image"
							: "m.file"
						: "m.text",
					url: selectedFile ? URL.createObjectURL(selectedFile) : null,
				}),
				getTs: () => Date.now(),
				getSender: () => client.getUserId(),
				getType: () => "m.room.message",
				isUploading: true,
			};

			setMatrixMessages((prev) => [...prev, mockEvent]);

			const textToSend = inputValue;
			const fileToSend = selectedFile;
			setInputValue("");
			clearFile();

			let content = { body: textToSend, msgtype: "m.text" };

			if (fileToSend) {
				const uploadRes = await client.uploadContent(fileToSend, {
					type: fileToSend.type,
					rawResponse: false,
					onlyContentUri: false,
				});

				let msgType = "m.file";
				if (fileToSend.type.startsWith("image/")) msgType = "m.image";
				else if (fileToSend.type.startsWith("video/")) msgType = "m.video";
				else if (fileToSend.type.startsWith("audio/")) msgType = "m.audio";

				content = {
					body: fileToSend.name,
					msgtype: msgType,
					url: uploadRes.content_uri,
					info: { mimetype: fileToSend.type, size: fileToSend.size },
				};

				if (textToSend.trim()) {
					await client.sendEvent(roomId, "m.room.message", {
						body: textToSend,
						msgtype: "m.text",
					});
				}
			}

			if (fileToSend || textToSend.trim()) {
				await client.sendEvent(roomId, "m.room.message", content);
				if (
					selectedId !== SAVED_MESSAGES_ID &&
					typeof selectedId === "number"
				) {
					api
						.post(
							"/notifyMessage",
							{ receiver_id: selectedId },
							{
								headers: {
									Authorization: `Bearer ${localStorage.getItem("token")}`,
								},
							},
						)
						.catch(console.error);
				}
			}
		} catch (err) {
			console.error("Matrix Send Error:", err);
			toast.error("خطا در ارسال پیام");
		}
	};

	const handleCreateGroup = async () => {
		if (selectedUsers.length < 2) {
			toast.error("لطفاً حداقل 2 کاربر انتخاب کنید");
			return;
		}
		if (!groupName.trim()) {
			toast.error("لطفاً نام گروه را وارد کنید");
			return;
		}
		try {
			const inviteList = selectedUsers.map((uid) => getMatrixId(uid));
			const res = await client.createRoom({
				visibility: "private",
				name: groupName,
				invite: inviteList,
				preset: "private_chat",
				is_direct: false,
				initial_state: [
					{
						type: "m.room.encryption",
						state_key: "",
						content: {
							algorithm: "m.megolm.v1.aes-sha2",
						},
					},
				],
			});
			await waitForRoomToAppear(res.room_id);
			setMatrixRooms([...client.getVisibleRooms()]);
			toast.success("گروه با موفقیت ساخته شد");
			setShowGroupModal(false);
			setSelectedUsers([]);
			setGroupName("");
			setSelectedId(res.room_id);
			setMobileChatOpen(true);
		} catch (err) {
			console.error("Group Creation Error:", err);
			toast.error("خطا در ساخت گروه");
		}
	};

	const toggleUserSelection = (uid) => {
		setSelectedUsers((prev) =>
			prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid],
		);
	};

	// ------------------------------------------------------------------
	// 7. SIDEBAR GENERATION
	// ------------------------------------------------------------------
	const getSidebarConversations = () => {
		if (!client) return specialConversations;

		const dmByUid = new Map();
		const groups = [];

		matrixRooms.forEach((room) => {
			const myMembership = room.getMyMembership();
			if (myMembership !== "join" && myMembership !== "invite") {
				return;
			}
			if (room.name === "Saved Messages") return;

			const others = getOtherMembers(room);
			const explicitNameEvent = room.currentState.getStateEvents(
				"m.room.name",
				"",
			);
			const hasExplicitName =
				explicitNameEvent && explicitNameEvent.getContent().name;

			if (others.length >= 2 || hasExplicitName) {
				groups.push({
					id: room.roomId,
					name: room.name || "گروه بدون نام",
					isSpecial: false,
					isGroup: true,
				});
				return;
			}

			if (others.length === 1) {
				const other = others[0];
				const uid = parseUidFromMatrixId(other.userId);
				if (!uid) return;

				const user = getUserById(uid);
				const displayName = user
					? `${user.fname} ${user.lname}`
					: other.name || `User ${uid}`;
				const ts = room.getLastActiveTimestamp?.() || 0;

				const existing = dmByUid.get(uid);
				if (!existing || ts > existing.ts) {
					dmByUid.set(uid, {
						id: uid,
						name: displayName,
						roomId: room.roomId,
						isSpecial: false,
						isGroup: false,
						profile_picture: user?.profile_picture,
						ts,
					});
				}
			}
		});

		const dms = Array.from(dmByUid.values());
		const finalList = [...specialConversations, ...groups, ...dms];

		const forcedIDs = [...recentConversations];
		if (
			typeof selectedId === "number" &&
			selectedId > 0 &&
			!forcedIDs.includes(selectedId)
		)
			forcedIDs.push(selectedId);

		forcedIDs.forEach((uid) => {
			const already = finalList.find((c) => c.id === uid);
			if (already) return;
			const user = getUserById(uid);
			if (user) {
				finalList.push({
					id: uid,
					name: `${user.fname} ${user.lname}`,
					isSpecial: false,
					isGroup: false,
					profile_picture: user?.profile_picture,
				});
			}
		});

		return finalList.filter(
			(v, i, a) => a.findIndex((t) => t.id === v.id) === i,
		);
	};

	const allConversations = getSidebarConversations();
	const selectedConv =
		allConversations.find((c) => c.id === selectedId) ||
		(typeof selectedId === "number" && selectedId > 0
			? getUserById(selectedId)
			: null);

	return (
		<div className="messages-layout-container">
			<div className="messages-userprofile-area">
				<UserProfile />
			</div>

			<div className="messages-main-area">
				<main className={`chat-area${mobileChatOpen ? " mobile-open" : ""}`}>
					<div className="chat-header-mobile">
						<button
							className="chat-header-back-btn"
							onClick={() => setMobileChatOpen(false)}
						>
							<ArrowRight size={28} color="#0052CC" variant="Linear" />
						</button>
						<div className="chat-header-user">
							<span className="chat-header-username">
								{selectedConv
									? selectedConv.name ||
										`${selectedConv.fname} ${selectedConv.lname}`
									: ""}
							</span>
						</div>
					</div>

					<div
						className="chat-header"
						onClick={() => selectedConv?.isGroup && setShowGroupInfoModal(true)}
					>
						{/* MAIN AVATAR */}
						<div className="avatar-circle">
							{selectedId === SAVED_MESSAGES_ID && (
								<Stickynote size={26} color="#7A7E9D" variant="Linear" />
							)}
							{selectedId === CHATBOT_ID && (
								<img src={chatbotAvatar} className="avatar-img" alt="chatbot" />
							)}
							{selectedConv && selectedConv.isGroup && (
								<People size={26} color="#7A7E9D" variant="Bold" />
							)}
							{typeof selectedId === "number" &&
								selectedId > 0 &&
								selectedConv &&
								!selectedConv.isGroup &&
								(selectedConv.profile_picture ? (
									<img
										src={selectedConv.profile_picture}
										className="avatar-img"
										alt="avatar"
									/>
								) : (
									<img
										src={defaultAvatar}
										className="avatar-img"
										alt="avatar"
									/>
								))}
						</div>

						{/* INFO */}
						<div className="chat-header-info" style={{ flex: 1 }}>
							<span className="chat-username">
								{selectedConv
									? selectedConv.name ||
										`${selectedConv.fname} ${selectedConv.lname}`
									: ""}
							</span>

							{selectedConv?.isGroup ? (
								<span className="chat-header-subtitle">
									{activeRoomMembers.length} عضو (کلیک برای مشاهده)
								</span>
							) : (
								<span
									className="chat-header-subtitle"
									style={{ fontWeight: 400, opacity: 0.7 }}
								></span>
							)}
						</div>

						{/* RIGHT ACTIONS */}
						<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
							{/* ✅ Delete/Leave Button */}
							{matrixRoomId &&
								selectedId !== CHATBOT_ID &&
								selectedId !== SAVED_MESSAGES_ID && (
									<button
										className="chat-header-action-btn"
										onClick={(e) => {
											e.stopPropagation();
											setShowDeleteModal(true);
										}}
										title={
											amIAdmin && selectedConv?.isGroup
												? "حذف گروه"
												: "ترک گفتگو"
										}
										style={{
											background: "transparent",
											border: "none",
											cursor: "pointer",
											color: "#FF5252",
											display: "flex",
											alignItems: "center",
											padding: "8px",
										}}
									>
										{amIAdmin && selectedConv?.isGroup ? (
											<Trash size={22} variant="Linear" />
										) : (
											<LogoutCurve size={22} variant="Linear" />
										)}
									</button>
								)}

							{/* Face Pile */}
							{selectedConv?.isGroup && activeRoomMembers.length > 0 && (
								<div className="header-face-pile">
									{activeRoomMembers.slice(0, 3).map((m, i) => (
										<div
											key={m.userId}
											title={m.name}
											style={{ zIndex: 5 - i }}
										>
											{m.avatar ? (
												<img
													src={m.avatar}
													className="header-face-pile-img"
													alt={m.name}
												/>
											) : (
												<div
													className="header-face-pile-img"
													style={{
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													<User size={16} color="#0052CC" variant="Bold" />
												</div>
											)}
										</div>
									))}
									{activeRoomMembers.length > 3 && (
										<div className="header-face-pile-count">
											+{activeRoomMembers.length - 3}
										</div>
									)}
								</div>
							)}
						</div>
					</div>

					<div className="chat-messages">
						{historyLoading && (
							<div className="history-loading-container">
								<div className="upload-spinner history-spinner"></div>
							</div>
						)}

						{!isReady && (
							<div className="chat-bubble them">Connecting to Chat...</div>
						)}

						{displayedMessages.map((msg, idx) => {
							const isGroup = selectedConv?.isGroup;
							const isThem = msg.sender === "them";
							const prevMsg = displayedMessages[idx - 1];
							const showName =
								isGroup &&
								isThem &&
								(!prevMsg ||
									prevMsg.senderId !== msg.senderId ||
									shouldShowDateSeparator(msg, prevMsg));

							return (
								<React.Fragment key={idx}>
									{shouldShowDateSeparator(msg, displayedMessages[idx - 1]) && (
										<div className="chat-date-separator">
											{formatDateInPersian(msg.fullDate)}
										</div>
									)}

									<div
										className={`chat-bubble ${msg.sender === "me" ? "me" : "them"} ${msg.isUploading ? "uploading" : ""}`}
									>
										{showName && (
											<span className="chat-sender-name">{msg.senderName}</span>
										)}

										{msg.text && <span className="chat-text">{msg.text}</span>}

										{msg.attachment && (
											<div className="chat-attachment">
												{msg.isUploading && (
													<div className="upload-overlay">
														<div className="upload-spinner"></div>
													</div>
												)}

												{msg.fileType.startsWith("image/") ? (
													<img
														src={msg.attachment}
														className="chat-attachment-img"
														alt="attachment"
														onClick={() =>
															window.open(msg.attachment, "_blank")
														}
													/>
												) : msg.fileType.startsWith("video/") ? (
													<video
														src={msg.attachment}
														controls
														className="chat-attachment-video"
														style={{ maxWidth: "100%", borderRadius: "8px" }}
													/>
												) : msg.fileType.startsWith("audio/") ? (
													<div className="chat-audio-container">
														<div className="audio-file-info">
															<div
																className={`file-icon-circle ${msg.sender === "me" ? "me-icon" : ""}`}
															>
																<Music size={20} color="#fff" variant="Bold" />
															</div>
															<span className="file-name" title={msg.fileName}>
																{msg.fileName || "Audio Message"}
															</span>
														</div>

														<audio
															src={msg.attachment}
															controls
															className="chat-attachment-audio"
														/>
													</div>
												) : (
													<div
														className="chat-attachment-file"
														onClick={() =>
															window.open(msg.attachment, "_blank")
														}
													>
														<div className="file-icon-circle">
															<AttachSquare
																size={24}
																color="#fff"
																variant="Bold"
															/>
														</div>
														<div className="file-info">
															<span className="file-name">{msg.fileName}</span>
															<span className="file-type">DOWNLOAD</span>
														</div>
													</div>
												)}
											</div>
										)}

										<span className="chat-time">{msg.time}</span>
									</div>
								</React.Fragment>
							);
						})}
						<div ref={messageContainerRef}></div>
					</div>

					<form className="chat-input-form" onSubmit={handleSend}>
						<input
							id="file-input"
							type="file"
							onChange={handleFileSelect}
							style={{ display: "none" }}
						/>

						{filePreview && (
							<div className="file-preview">
								{selectedFile && selectedFile.type.startsWith("image/") ? (
									<img
										src={filePreview}
										className="file-preview-img"
										alt="preview"
									/>
								) : (
									<div className="file-preview-generic">
										<AttachSquare size={32} color="#555" variant="Bold" />
										<span className="file-preview-name">
											{selectedFile?.name}
										</span>
									</div>
								)}
								<button
									type="button"
									className="file-preview-remove"
									onClick={clearFile}
								>
									×
								</button>
							</div>
						)}

						<button
							type="button"
							className="chat-attachment-btn"
							onClick={() => document.getElementById("file-input").click()}
						>
							<AttachSquare size={22} color="#7A7E9D" variant="Linear" />
						</button>

						<input
							className="chat-input"
							type="text"
							placeholder={
								selectedId === CHATBOT_ID
									? "با چت‌بات صحبت کنید..."
									: "پیامی بنویسید..."
							}
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
						/>

						<button
							className="chat-send-btn"
							type="submit"
							disabled={isLoading}
						>
							<ArrowUp size={22} variant="Linear" />
						</button>
					</form>
				</main>

				<aside
					className={`messages-sidebar${mobileChatOpen ? " mobile-hide" : ""}`}
				>
					<ul className="conversation-list">
						{!isReady ? (
							<li className="conversation-item" style={{ opacity: 0.5 }}>
								<span className="conversation-name">در حال بروزرسانی...</span>
							</li>
						) : allConversations.length === 0 ? (
							<li className="conversation-item" style={{ opacity: 0.5 }}>
								<span className="conversation-name">هیچ گفتگویی ندارید</span>
							</li>
						) : (
							allConversations.map((conv) => (
								<li
									key={String(conv.id)}
									className={`conversation-item${selectedId === conv.id ? " selected" : ""}`}
									onClick={() => {
										setSelectedId(conv.id);
										setMobileChatOpen(true);
									}}
								>
									<div className="avatar-circle">
										{conv.isSpecial && conv.id === 0 && (
											<Stickynote size={28} color="#7A7E9D" variant="Linear" />
										)}
										{conv.isSpecial && conv.id === -1 && (
											<img
												src={chatbotAvatar}
												alt="chatbot"
												className="avatar-img"
											/>
										)}

										{conv.isGroup && (
											<People size={26} color="#7A7E9D" variant="Bold" />
										)}

										{!conv.isSpecial &&
											!conv.isGroup &&
											(() => {
												const user = getUserById(conv.id);
												return user && user.profile_picture ? (
													<img
														src={user.profile_picture}
														alt={`${user.fname} ${user.lname}`}
														className="avatar-img"
													/>
												) : (
													<img
														src={defaultAvatar}
														alt="avatar"
														className="avatar-img"
													/>
												);
											})()}
									</div>

									<span className="conversation-name">{conv.name}</span>
								</li>
							))
						)}
					</ul>

					<div className="sidebar-header">
						<button
							className="new-conversation-btn"
							onClick={() => setShowNewConversationModal(true)}
						>
							<Add size={18} /> مکالمه جدید
						</button>
						<button
							className="new-group-btn"
							onClick={() => setShowGroupModal(true)}
						>
							<Add size={18} /> گروه جدید
						</button>
					</div>

					{showNewConversationModal && (
						<div className="group-modal-overlay">
							<div className="group-modal-content">
								<h3 className="group-modal-title">شروع مکالمه جدید</h3>

								<div className="group-users-section">
									<p className="group-users-label">مخاطب را انتخاب کنید</p>

									{teamUsers.map((user) => {
										if (Number(user.uid) === Number(userData?.uid)) return null;

										return (
											<div
												key={user.uid}
												className="group-user-item"
												onClick={() => {
													setSelectedId(user.uid);
													setShowNewConversationModal(false);
													setMobileChatOpen(true);
												}}
											>
												<div
													style={{
														width: "32px",
														height: "32px",
														borderRadius: "50%",
														background: "#667eea",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														color: "white",
														fontSize: "14px",
														marginLeft: "12px",
														fontWeight: "bold",
														overflow: "hidden",
													}}
												>
													{user.profile_picture ? (
														<img
															src={user.profile_picture}
															alt="avatar"
															style={{
																width: "100%",
																height: "100%",
																objectFit: "cover",
															}}
														/>
													) : (
														user.fname?.[0] || "?"
													)}
												</div>

												<span className="group-user-name">
													{user.fname} {user.lname}
												</span>
											</div>
										);
									})}
								</div>

								<div className="group-modal-actions">
									<button
										className="group-cancel-btn"
										onClick={() => setShowNewConversationModal(false)}
										style={{ width: "100%" }}
									>
										انصراف
									</button>
								</div>
							</div>
						</div>
					)}

					{showGroupModal && (
						<div className="group-modal-overlay">
							<div className="group-modal-content">
								<h3 className="group-modal-title">ساخت گروه جدید</h3>

								<input
									type="text"
									className="group-name-input"
									placeholder="نام گروه را وارد کنید"
									value={groupName}
									onChange={(e) => setGroupName(e.target.value)}
								/>

								<div className="group-users-section">
									<p className="group-users-label">
										انتخاب اعضا ({selectedUsers.length} نفر انتخاب شده)
									</p>

									{teamUsers.map((user) => {
										const isCurrentUser =
											Number(user.uid) === Number(userData?.uid);
										if (isCurrentUser) return null;

										return (
											<div
												key={user.uid}
												className={`group-user-item ${selectedUsers.includes(user.uid) ? "selected" : ""}`}
												onClick={() => toggleUserSelection(user.uid)}
											>
												<div className="group-user-checkbox">
													{selectedUsers.includes(user.uid) && "✓"}
												</div>
												<span className="group-user-name">
													{user.fname} {user.lname}
												</span>
											</div>
										);
									})}
								</div>

								<div className="group-modal-actions">
									<button
										className="group-create-btn"
										onClick={handleCreateGroup}
									>
										ساخت گروه
									</button>
									<button
										className="group-cancel-btn"
										onClick={() => {
											setShowGroupModal(false);
											setSelectedUsers([]);
											setGroupName("");
										}}
									>
										انصراف
									</button>
								</div>
							</div>
						</div>
					)}

					{showGroupInfoModal && (
						<div className="group-modal-overlay">
							<div className="group-modal-content">
								<h3 className="group-modal-title">اعضای گروه</h3>

								<div className="group-users-section">
									{activeRoomMembers.map((member) => (
										<div
											key={member.userId}
											className="group-user-item"
											style={{ cursor: "default" }}
										>
											<div
												style={{
													width: "36px",
													height: "36px",
													borderRadius: "50%",
													background: "#667eea",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													color: "white",
													fontSize: "14px",
													marginLeft: "12px",
													fontWeight: "bold",
													overflow: "hidden",
												}}
											>
												{member.avatar ? (
													<img
														src={member.avatar}
														alt="avatar"
														style={{
															width: "100%",
															height: "100%",
															objectFit: "cover",
														}}
													/>
												) : (
													<User size={18} color="#fff" variant="Bold" />
												)}
											</div>

											<span className="group-user-name">
												{member.name}
												{member.userId === client.getUserId() && " (شما)"}
											</span>

											{member.powerLevel >= 50 && (
												<span className="group-info-role">مدیر</span>
											)}
										</div>
									))}
								</div>

								<div className="group-modal-actions">
									<button
										className="group-cancel-btn"
										onClick={() => setShowGroupInfoModal(false)}
										style={{ width: "100%" }}
									>
										بستن
									</button>

									<button
										className="group-cancel-btn"
										onClick={() => {
											setShowGroupInfoModal(false);
											setShowDeleteModal(true);
										}}
										style={{ color: "#FF5252" }}
									>
										{amIAdmin ? "حذف گروه" : "خروج از گروه"}
									</button>
								</div>
							</div>
						</div>
					)}

					{showDeleteModal && (
						<div className="group-modal-overlay">
							<div
								className="group-modal-content"
								style={{ maxWidth: "400px" }}
							>
								<h3 className="group-modal-title" style={{ color: "#FF5252" }}>
									{amIAdmin && selectedConv?.isGroup ? "حذف گروه" : "ترک گفتگو"}
								</h3>
								<p
									style={{
										textAlign: "center",
										color: "#fff",
										marginBottom: "24px",
									}}
								>
									{amIAdmin && selectedConv?.isGroup
										? "با حذف گروه، تمام اعضا خارج شده و تاریخچه پاک می‌شود."
										: "آیا مطمئن هستید که می‌خواهید از این گفتگو خارج شوید؟"}
									<br />
									<span style={{ fontSize: "12px", opacity: 0.7 }}>
										این عمل غیرقابل بازگشت است.
									</span>
								</p>

								<div className="group-modal-actions">
									<button
										className="group-create-btn"
										onClick={handleDeleteOrLeave}
										disabled={isDeleting}
										style={{
											background: "#FF5252",
											opacity: isDeleting ? 0.7 : 1,
										}}
									>
										{isDeleting ? "در حال حذف..." : "بله، انجام بده"}
									</button>
									<button
										className="group-cancel-btn"
										onClick={() => setShowDeleteModal(false)}
										disabled={isDeleting}
									>
										خیر
									</button>
								</div>
							</div>
						</div>
					)}
				</aside>
			</div>
		</div>
	);
}

export default Messages;
